import { COLORS } from './format';
import type { InboundRequest } from '../../types/taActionBoard';

/**
 * What the Scheduling tab did about "please propose times" (Sofia, Sep 2026).
 *
 * Green when the panel's free slots are already IN the reply — the proxy read the role's case-panel
 * calendars and swapped the "I will come back with times" promise for the actual list. Amber when
 * it could not, naming WHY, because every reason has a different fix: a stage the pool does not
 * schedule from (move them in Lever), a case panel that already exists, a snooze, a role with no
 * panel configured, no free slot in the window, or calendars that could not be read.
 *
 * Renders nothing for intents that do not ask for times: the proxy sends `proposal: null` there.
 */
export function ProposalBox({ proposal }: { proposal: InboundRequest['proposal'] }) {
  if (!proposal) return null;
  const good = proposal.kind === 'proposed';
  return (
    <div
      className="rounded-lg p-3 text-sm"
      data-testid="proposal-box"
      style={
        good
          ? { backgroundColor: COLORS.greenBg, color: COLORS.greenText }
          : { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }
      }
    >
      {/* The amber messages open with their own "No times added:" lead, so only the good case gets a label. */}
      {good && <span className="font-semibold">Times included. </span>}
      {proposal.message}
    </div>
  );
}

/**
 * Whether the role is still open, per the hiring plan in Airtable — provenance for the "is the role
 * still open?" reply and for the fact every draft was told. One muted line; nothing when unknown.
 */
export function RoleStatusLine({ status }: { status: InboundRequest['roleStatus'] }) {
  if (!status || status.value === 'unknown') return null;
  const word = { open: 'open', closed: 'closed', hold: 'on hold' }[status.value];
  return (
    <p className="text-xs text-gray-500" data-testid="role-status-line">
      <span className="font-semibold">Hiring plan:</span> role is {word}
      {status.label ? ` (“${status.label}”, ${status.source === 'airtable' ? 'Airtable' : 'Lever'})` : ''}
    </p>
  );
}
